import { ArenaDeckSelectCard } from "./arena-deck-select-card";
import { useState, useCallback, useMemo } from "react";
import { RouteNames } from "@/types/route-names";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import { useItemDecks } from "@/hooks/queries/use-item-decks";
import { useInventoryPokemons } from "@/hooks/queries/use-inventory-pokemons";
import { useAuth } from "@/providers/auth-provider";
import { Alert } from "@/components/alert/alert";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import type { ItemDeckData } from "types/web-app";
import { useDuel } from "@/providers/duel-provider";

/**
 * Arena Deck Selection View - Select an item deck before entering the arena queue (core-pvp-revamp).
 */
export const ArenaTeamSelectView = () => {
  const { setCurrentRoute, previousRoute } = useSPAContext();
  const { getAccessToken, state: authState } = useAuth();
  const { enqueue } = useDuel();

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const { data: decks, isLoading, isError, refetch } = useItemDecks();
  const { data: ownedPokemons } = useInventoryPokemons();
  const [isEnqueuing, setIsEnqueuing] = useState(false);
  const [forceBot, setForceBot] = useState(false);

  const pokemonById = useMemo(() => {
    const map = new Map<number, NonNullable<typeof ownedPokemons>[number]>();
    for (const poke of ownedPokemons ?? []) map.set(poke.id, poke);
    return map;
  }, [ownedPokemons]);

  const handleDeckSelect = useCallback(
    async (deck: ItemDeckData) => {
      if (isEnqueuing) return;

      if (!deck.is_valid_for_match) {
        Alert("This deck is not ready for a match (needs 16 items, 8 Pokémon, and enough energy placed)");
        return;
      }

      const accessToken = getAccessToken();
      if (!accessToken || authState.status !== "authenticated") {
        Alert("Authentication error. Please try again");
        return;
      }

      const playerId = authState.user?.player_id;
      if (!playerId) {
        Alert("Authentication error. Please try again");
        return;
      }

      const deckPokemons = deck.poke_slots
        .map((id) => pokemonById.get(id))
        .filter((p): p is NonNullable<typeof p> => !!p);

      setIsEnqueuing(true);
      try {
        setCurrentRoute(RouteNames.DUEL_QUEUE);
        await enqueue(accessToken, playerId, "RANKED_1V1", deck.id, deckPokemons, forceBot);
      } catch (error) {
        console.error("Failed to start duel:", error);
        Alert("Failed to start duel");
      } finally {
        setIsEnqueuing(false);
      }
    },
    [isEnqueuing, getAccessToken, authState, setCurrentRoute, enqueue, pokemonById, forceBot],
  );

  return (
    <div className="flex flex-col h-full w-full relative overflow-y-auto bg-pk-night-1/90 backdrop-blur-md pb-24">
      {/* Header - aligned with play-menu */}
      <div className="relative z-10 flex items-center justify-between mt-6 xs:mt-8">
        <div className="flex flex-col ml-4 p-2">
          <div className="relative">
            <h1 className="text-3xl xs:text-4xl font-black text-white uppercase tracking-tight leading-none drop-shadow-lg">
              SELECT ARENA DECK
            </h1>
            <div className="h-1 w-12 xs:w-16 bg-pk-yellow-1 mt-1 skew-x-[-20deg]" />
          </div>
        </div>
      </div>

      {/* Content - aligned with play-menu + inventory frame */}
      <div className="flex flex-col gap-4 xs:gap-6 px-4 pt-4 xs:pt-6 relative z-10 mx-auto w-full max-w-md">
        {/* DEV: Force bot toggle */}
        {import.meta.env.DEV && (
          <button
            onClick={() => setForceBot((v) => !v)}
            className={`self-start flex items-center gap-2 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide border transition-colors ${
              forceBot
                ? "bg-pk-yellow-1 border-pk-yellow-1 text-black"
                : "bg-transparent border-pk-gray-4 text-pk-gray-4"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${forceBot ? "bg-black" : "bg-pk-gray-4"}`} />
            vs Bot (instant)
          </button>
        )}

        {/* Decks container - inventory-style frame */}
        <div
          className="border-4 border-gray-900 relative flex flex-col overflow-hidden rounded-lg shadow-cartoon-sm min-h-[200px] max-h-[60vh]"
          style={{ backgroundColor: "#3b2c35" }}
        >
          <div className="absolute bg-black/70 inset-0 border-4 border-white/20 pointer-events-none z-10 rounded-lg" />
          <div
            className="absolute inset-0 z-0 pointer-events-none rounded-lg"
            style={{
              backgroundImage: "url(/assets/app-ui/glass-2.png)",
              backgroundSize: "cover",
              backgroundPosition: "left",
              backgroundRepeat: "repeat",
              mixBlendMode: "multiply",
              opacity: 0.35,
            }}
          />
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 animate-pulse relative z-20">
              <span className="text-white font-semibold text-lg">
                Loading decks...
              </span>
              <p className="text-pk-gray-4 text-xs mt-2">
                This should only take a moment
              </p>
            </div>
          )}
          {!isLoading && isError && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center relative z-20">
              <span className="text-white font-semibold text-lg">
                Failed to load decks
              </span>
              <SimpleButton variant="gray" onClick={() => refetch()}>
                Retry
              </SimpleButton>
            </div>
          )}
          {!isLoading && !isError && decks && decks.length > 0 && (
            <DeckList
              decks={decks}
              pokemonById={pokemonById}
              handleDeckClick={handleDeckSelect}
              isEnqueuing={isEnqueuing}
            />
          )}
          {!isLoading && !isError && decks && decks.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center relative z-20">
              <span className="text-white font-semibold text-lg">
                No decks yet
              </span>
              <p className="text-pk-gray-4 text-sm mt-2 max-w-sm">
                Build a deck with 16 items and 8 Pokémon to play in the arena.
              </p>
              <SimpleButton
                variant="blue"
                className="mt-4"
                onClick={() => setCurrentRoute(RouteNames.ITEM_DECK_LIST)}
              >
                Create deck
              </SimpleButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DeckList = ({
  decks,
  pokemonById,
  handleDeckClick,
  isEnqueuing,
}: {
  decks: ItemDeckData[];
  pokemonById: Map<number, { id: number; base_pokemon_name: string; is_shiny?: boolean }>;
  handleDeckClick: (deck: ItemDeckData) => void;
  isEnqueuing: boolean;
}) => {
  return (
    <div className="h-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 relative z-20 flex flex-col gap-3">
      {decks?.map((deck) => {
        const isBlocked = !deck.is_valid_for_match;
        return (
          <div
            key={deck.id}
            className={`relative ${
              isEnqueuing ? "opacity-50 pointer-events-none" : ""
            } ${isBlocked ? "pointer-events-none" : ""}`}
          >
            {isBlocked && (
              <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-black/60 backdrop-blur-[2px]">
                <span className="text-white font-bold text-sm uppercase tracking-wide drop-shadow-md text-center px-2">
                  Needs 16 items, 8 Pokémon, and enough energy
                </span>
              </div>
            )}
            <div className={isBlocked ? "opacity-60" : ""}>
              <ArenaDeckSelectCard
                deck={deck}
                pokemonById={pokemonById}
                {...(!isBlocked && { onClick: handleDeckClick })}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
